import React, {useCallback, useEffect} from 'react';
import {Grid, Typography} from '@mui/material';
import {useModal} from 'common/hooks/useModal';
import {DialogTypes} from 'common/names';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import Dialog from 'components/Dialog';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {useFieldValueGetter} from 'components/hook-form/useFieldValueGetter';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import FormikTextField from 'components/inputs/FormikTextField';
import {useTranslation} from 'react-i18next';
import ThreatIntelCacheDialog from './ThreatIntelCacheDialog';
import Panel from 'components/Panel';
import {Offset} from 'components/Offset';
import Button from 'components/Button';
import {ReadOnlyProvider} from 'common/context/ReadOnlyContext';
import {
   IThreatIntelligenceSettings,
   ThreatIntelligenceSettings,
   ThreatIntelligenceSettingsNames,
} from 'types/diplomat/sftp/ThreatIntelligenceSettings';
import {useGetApi} from 'common/hooks/useGetApi';
import api from 'api';
import {usePostApi} from 'common/hooks/usePostApi';
import {castToSend} from 'common/yup/castToSend';

type Props = {
   onClose: () => void;
};

const ThreatIntelligenceSettingsForm = ({onClose}: Props) => {
   const {t} = useTranslation();
   const {open: isOpen, handleOpen: openModal, handleClose: closeModal} = useModal();

   const getValue = useFieldValueGetter<IThreatIntelligenceSettings>();

   const openCacheDialog = () => {
      openModal();
   };

   return (
      <>
         <Typography>
            {t(
               'Threat intelligence is evidence-based information about cyber attacks, sourced from Open Source and commercial blocklists to identify potential threats. Configure how Diplomat MFT will use Threat Intelligence to prevent potentially malicious connections.'
            )}
         </Typography>
         <FormikCheckbox
            name={ThreatIntelligenceSettingsNames.enabled}
            label={t('Enable Threat Intelligence')}
         />
         <Panel>
            <ReadOnlyProvider value={!getValue(ThreatIntelligenceSettingsNames.enabled)}>
               <Grid container spacing={1} sx={{ml: 2}}>
                  <Grid item xs={12}>
                     <FormikCheckbox
                        name={ThreatIntelligenceSettingsNames.blockKnownAttacker}
                        label={t('Block known attackers')}
                     />
                  </Grid>
                  <Grid item xs={12}>
                     <FormikCheckbox
                        name={ThreatIntelligenceSettingsNames.blockKnownSpam}
                        label={t('Block known abusers')}
                     />
                  </Grid>
                  <Grid item xs={12}>
                     <FormikCheckbox
                        name={ThreatIntelligenceSettingsNames.blockTor}
                        label={t('Block connections from Tor')}
                     />
                  </Grid>
                  <Grid item xs={12}>
                     <FormikCheckbox
                        name={ThreatIntelligenceSettingsNames.blockVPN}
                        label={t('Block VPN connections')}
                     />
                  </Grid>
                  <Grid item xs={12}>
                     <FormikCheckbox
                        name={ThreatIntelligenceSettingsNames.blockProxy}
                        label={t('Block Proxy connections')}
                     />
                  </Grid>
                  <Grid item xs={12}>
                     <FormikCheckbox
                        name={ThreatIntelligenceSettingsNames.blockDataCenter}
                        label={t('Block connections from Data Centers')}
                     />
                  </Grid>

                  <Grid item xs={4}>
                     <FormikCheckbox
                        name={ThreatIntelligenceSettingsNames.useThreatThreshold}
                        label={t('Use threat threshold to block connections')}
                     />
                  </Grid>
                  <Grid item xs={2}>
                     <FormikTextField
                        type="number"
                        name={ThreatIntelligenceSettingsNames.threatThreshold}
                        label={t('Max threat threshold')}
                        disabled={!getValue(ThreatIntelligenceSettingsNames.useThreatThreshold)}
                     />
                  </Grid>
                  <Offset xs={6} />

                  <Grid item xs={4}>
                     <FormikCheckbox
                        name={ThreatIntelligenceSettingsNames.useTrustThreshold}
                        label={t('Use trust threshold to block connections')}
                     />
                  </Grid>
                  <Grid item xs={2}>
                     <FormikTextField
                        type="number"
                        name={ThreatIntelligenceSettingsNames.trustThreshold}
                        label={t('Min trust threshold')}
                        disabled={!getValue(ThreatIntelligenceSettingsNames.useTrustThreshold)}
                     />
                  </Grid>

                  <Grid item xs={12} sx={{textAlign: 'center'}}>
                     <Button onClick={openCacheDialog}>{t('Manage Cache')}</Button>
                  </Grid>
               </Grid>
            </ReadOnlyProvider>
         </Panel>
         <FormFooterButtons cancel={onClose} submitLabel={t('Save')} />

         {isOpen && <ThreatIntelCacheDialog onClose={closeModal} />}
      </>
   );
};

const options = {
   transformData(data: IThreatIntelligenceSettings) {
      return ThreatIntelligenceSettings.cast(data);
   },
};

export const ThreatIntelligenceSettingsDialog = ({onClose}: Props) => {
   const {t} = useTranslation();
   const {request, state: initialValues} = useGetApi<IThreatIntelligenceSettings>(
      api.threatintelligence,
      options
   );

   useEffect(request, [request]);

   const {request: saveTI} = usePostApi(api.threatintelligence, {
      successMessage: t('Threat Intelligence Settings saved successfully.'),
      updateValues: true,
   });

   const onSubmit = useCallback(
      (
         values: IThreatIntelligenceSettings,
         helpers: FormOnSubmitHelpers<IThreatIntelligenceSettings>
      ) => {
         saveTI(JSON.stringify(castToSend(ThreatIntelligenceSettings, values)), {helpers});
      },
      [saveTI]
   );

   return (
      <Dialog
         maxWidth="md"
         header={DialogTypes.ThreatIntelligenceSettings.dialogName}
         close={onClose}
      >
         {initialValues && (
            <Formik initialValues={initialValues} onSubmit={onSubmit}>
               <ThreatIntelligenceSettingsForm onClose={onClose} />
            </Formik>
         )}
      </Dialog>
   );
};
